import { z } from "zod";
import { REPORT_TOOL_KEYS } from "../constants/report-tools";

/**
 * Frequências e dias existem apenas na UI — a API recebe
 * INTERVAL/CRON (ver `mappers/frequency.ts`).
 */
export const FREQUENCIES = [
  "daily",
  "weekly",
  "biweekly",
  "monthly",
  "quarterly",
] as const;

export const WEEKDAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
] as const;

export const CHANNELS = ["email", "whatsapp"] as const;

export type Frequency = (typeof FREQUENCIES)[number];
export type Weekday = (typeof WEEKDAYS)[number];

const TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * Valida por dígitos em vez de máscara, porque o mesmo campo recebe tanto o
 * que o usuário digita — `(19) 9 9999-9999` — quanto o que volta da API já
 * normalizado (`5519999999999`).
 *
 * Aceita 10 ou 11 dígitos (DDD + fixo/celular), com ou sem o DDI 55.
 */
export function isValidPhone(value: string): boolean {
  const digits = value.replace(/\D/g, "");
  const national =
    digits.startsWith("55") && digits.length >= 12 ? digits.slice(2) : digits;

  return national.length === 10 || national.length === 11;
}

export const scheduleFormSchema = z
  .object({
    toolKey: z.enum(REPORT_TOOL_KEYS, {
      required_error: "report-required",
    }),
    frequency: z.enum(FREQUENCIES, {
      required_error: "frequency-required",
    }),
    weekdays: z.array(z.enum(WEEKDAYS)),
    time: z.string().regex(TIME_REGEX, "time-invalid"),
    timezone: z.string().min(1, "timezone-required"),
    channels: z.array(z.enum(CHANNELS)).min(1, "channels-required"),
    // Único telefone do agendamento. É o que vai em `recipients` quando o
    // canal WhatsApp está ativo — o e-mail o backend extrai do usuário logado.
    phone: z.string().optional().or(z.literal("")),
  })
  .superRefine((values, ctx) => {
    // Exatamente um dia: a UI é de seleção única, mas o campo continua array
    // porque é o que o cron da API consome (ver `mappers/frequency.ts`).
    if (values.frequency === "weekly" && values.weekdays.length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["weekdays"],
        message: "weekdays-required",
      });
    }

    // Telefone é obrigatório quando o WhatsApp está marcado: é o valor
    // enviado em `recipients`.
    if (values.channels.includes("whatsapp")) {
      if (!values.phone || !isValidPhone(values.phone)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["phone"],
          message: "phone-invalid",
        });
      }
    }
  });

export type ScheduleFormValues = z.infer<typeof scheduleFormSchema>;
